import React from "react";
import {
  Grid,
  MenuItem,
  TextField,
  Typography
} from "@material-ui/core";
import { ICustomQuestion } from "../utils";

export interface ICustomFormAnswer {
  questionId: string;
  answer: string;
}

interface CustomQuestionsFieldsProps {
  customQuestions: ICustomQuestion[];
  answers: ICustomFormAnswer[];
  onChange: (answers: ICustomFormAnswer[]) => void;
  readOnly?: boolean;
}

export const CustomQuestionsFields: React.FC<CustomQuestionsFieldsProps> = ({
  customQuestions,
  answers,
  onChange,
  readOnly = false
}) => {
  if (!customQuestions || customQuestions.length === 0) return null;

  const visibleQuestions = customQuestions.filter(
    (q) => q.visibleToBusinessCustomer !== false
  );

  if (!visibleQuestions.length) return null;

  const getAnswerForQuestion = (questionId: string): string => {
    const found = answers.find((a) => a.questionId === questionId);
    return found?.answer ?? "";
  };

  const setAnswerForQuestion = (questionId: string, answer: string) => {
    const newAnswers = answers.filter((a) => a.questionId !== questionId);

    if (answer !== undefined && answer !== "") {
      newAnswers.push({ questionId, answer });
    }

    onChange(newAnswers);
  };

  const renderField = (q: ICustomQuestion, value: string) => {
    const questionId = q.id || `q_${q.questionText}`;

    switch (q.questionType) {
      case "TEXT":
        return (
          <TextField
            fullWidth
            multiline
            rows={3}
            value={value}
            placeholder={q.questionText}
            onChange={(e) =>
              setAnswerForQuestion(questionId, e.target.value)
            }
          />
        );

      case "YES_NO":
        return (
          <TextField
            fullWidth
            select
            value={value}
            onChange={(e) =>
              setAnswerForQuestion(questionId, e.target.value)
            }
          >
            <MenuItem value="Yes">Yes</MenuItem>
            <MenuItem value="No">No</MenuItem>
          </TextField>
        );

      case "DROPDOWN":
        if (!q.options || !q.options.length) return null;

        return (
          <TextField
            fullWidth
            select
            value={value}
            onChange={(e) =>
              setAnswerForQuestion(questionId, e.target.value)
            }
          >
            {q.options.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </TextField>
        );

      default:
        return null;
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          Additional Questions
        </Typography>
      </Grid>

      {visibleQuestions.map((q) => {
        const questionId = q.id || `q_${q.questionText}`;
        const value = getAnswerForQuestion(questionId);

        const isText = q.questionType === "TEXT";

        return (
          <Grid
            item
            xs={12}
            sm={12}
            md={isText ? 12 : 6}
            lg={isText ? 12 : 6}
            key={questionId}
          >
            <Typography className="opacity60 padding-bottom--small">
              {q.questionText}
              {q.isRequired && <span>&nbsp;*</span>}
            </Typography>

            {readOnly ? (
              <Typography variant="body1">
                {value || "-"}
              </Typography>
            ) : (
              renderField(q, value)
            )}
          </Grid>
        );
      })}
    </Grid>
  );
};